import React from 'react';

export type MatrixState = Record<string, string[]>;

const applyCustomerFamilies = (
  matrix: MatrixState,
  customerId: string,
  familyIds: string[],
  enabled: boolean,
): MatrixState => {
  const currentFamilyIds = new Set(matrix[customerId] ?? []);
  familyIds.forEach((familyId) => {
    if (enabled) {
      currentFamilyIds.add(familyId);
    } else {
      currentFamilyIds.delete(familyId);
    }
  });

  return {
    ...matrix,
    [customerId]: Array.from(currentFamilyIds),
  };
};

export const useProductRangeMatrix = (initialMatrix: MatrixState = {}) => {
  const [matrix, setMatrix] = React.useState<MatrixState>(initialMatrix);

  const setCustomerSelections = React.useCallback((customerId: string, familyIds: string[]) => {
    setMatrix((previous) => ({
      ...previous,
      [customerId]: familyIds,
    }));
  }, []);

  const removeCustomer = React.useCallback((customerId: string) => {
    setMatrix((previous) => {
      const next = { ...previous };
      delete next[customerId];
      return next;
    });
  }, []);

  const clearMatrix = React.useCallback(() => {
    setMatrix({});
  }, []);

  const updateCustomerFamilies = React.useCallback((customerId: string, familyIds: string[], enabled: boolean) => {
    setMatrix((previous) => applyCustomerFamilies(previous, customerId, familyIds, enabled));
  }, []);

  const updateSelectedCustomers = React.useCallback((customerIds: string[], familyIds: string[], enabled: boolean) => {
    setMatrix((previous) => customerIds.reduce(
      (next, customerId) => applyCustomerFamilies(next, customerId, familyIds, enabled),
      previous,
    ));
  }, []);

  return {
    matrix,
    setCustomerSelections,
    removeCustomer,
    clearMatrix,
    updateCustomerFamilies,
    updateSelectedCustomers,
  };
};
